// This comes from https://github.com/NiklasMencke/nextjs-breadcrumbs/blob/main/src/index.tsx which has been abandoned.

/** @format */

import React, { CSSProperties, useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { createGlobalStyle } from 'styled-components'

// This is here because we're only allowed to import global css in _app.js
const BreadCrumbsStyles = createGlobalStyle(
  () => `
.breadcrumb {
  list-style: none;
  overflow: hidden;
  font: 16px Sans-Serif;
}
.breadcrumb li {
  float: left;
}
.breadcrumb li a {
  color: rgb(0, 0, 0);
  text-decoration: none;
  padding: 10px 0 10px 50px;
  background: rgb(230, 224, 224);
  position: relative;
  display: block;
  float: left;
}

.breadcrumb li a::after {
  content: ' ';
  display: block;
  width: 0;
  height: 0;
  border-top: 50px solid transparent; /* Go big on the size, and let overflow hide */
  border-bottom: 50px solid transparent;
  border-left: 30px solid rgb(230, 224, 224);
  position: absolute;
  top: 50%;
  margin-top: -50px;
  left: 100%;
  z-index: 2;
}

.breadcrumb li a::before {
  content: ' ';
  display: block;
  width: 0;
  height: 0;
  border-top: 50px solid transparent;
  border-bottom: 50px solid transparent;
  border-left: 30px solid white;
  position: absolute;
  top: 50%;
  margin-top: -50px;
  margin-left: 1px;
  left: 100%;
  z-index: 1;
}

.breadcrumb li:first-child a {
  padding-left: 20px;
  pointer-events: visible !important;
  cursor: pointer !important;
}
.breadcrumb li:nth-child(2) a {
  background: rgb(224, 233, 233);
}
.breadcrumb li:nth-child(2) a:after {
  border-left-color: rgb(224, 233, 233);
}
.breadcrumb li:nth-child(3) a {
  background: rgb(224, 233, 233);
}
.breadcrumb li:nth-child(3) a:after {
  border-left-color: rgb(224, 233, 233);
}
.breadcrumb li:nth-child(4) a {
  background: rgb(224, 233, 233);
}
.breadcrumb li:nth-child(4) a:after {
  border-left-color: rgb(224, 233, 233);
}
.breadcrumb li:nth-child(5) a {
  background: rgb(224, 233, 233);
}
.breadcrumb li:nth-child(5) a:after {
  border-left-color: rgb(224, 233, 233);
}
.breadcrumb li:last-child a {
  background: rgb(238, 235, 232) !important;
  color: black;
  pointer-events: none;
  cursor: default;
  padding-right: 20px;
}
.breadcrumb li:last-child a::after {
  border: 0;
}

.breadcrumb li a:hover {
  background: rgb(194, 226, 221);
}
.breadcrumb li a:hover:after {
  border-left-color: rgb(194, 226, 221) !important;
}

`,
)

/**
 * Takes an URL String and removes query params and hash params
 *
 * @param url - The URL string
 * @returns The transformed URL string
 *
 */
const getPathFromUrl = (url: string): string => {
  return url.split(/[?#]/)[0]
}

/**
 * Takes a breadcrumb title (from url path) and replaces
 * special chars to more readable chars
 *
 * @param title - The breadcrumb title
 * @returns The transformed title or the result of the custom transformLabel function
 *
 */
const convertBreadcrumb = (
  title: string,
  toUpperCase: boolean | undefined,
  replaceCharacterList: Array<CharacterMap> | undefined,
  transformLabel?: ((title: string) => React.ReactNode) | undefined,
): React.ReactNode => {
  let transformedTitle = getPathFromUrl(title)

  if (transformLabel) {
    return transformLabel(transformedTitle)
  }

  if (replaceCharacterList) {
    for (let i = 0; i < replaceCharacterList.length; i++) {
      transformedTitle = transformedTitle.replaceAll(
        replaceCharacterList[i].from,
        replaceCharacterList[i].to,
      )
    }
  }

  // decode for utf-8 characters and return ascii.
  return toUpperCase ? decodeURI(transformedTitle).toUpperCase() : decodeURI(transformedTitle)
}

export interface Breadcrumb {
  /** Breadcrumb title. Example: 'blog-entries' */
  breadcrumb: string

  /** The URL which the breadcrumb points to. Example: 'blog/blog-entries' */
  href: string
}

export interface CharacterMap {
  /** The source character or character pattern that should be replaced (e.g. 'ae') */
  from: string

  /** The replacement into which the character should be replaced. */
  to: string
}

export interface BreadcrumbsProps {
  /** If true, the default styles are used.
   * Make sure to import the CSS in _app.js
   * Example: true Default: false */
  useDefaultStyle?: boolean

  /** The title for the very first breadcrumb pointing to the root directory. Example: '/' Default: 'HOME' */
  rootLabel?: string | null

  /** Boolean indicator whether the root label should be omitted. Example: true Default: false */
  omitRootLabel?: boolean

  /** Boolean indicator if the labels should be displayed as uppercase. Example: true Default: false */
  labelsToUppercase?: boolean | undefined

  /** Array containing a list of specific characters that should be replaced in the label. This can be useful to convert special characters such as vowels. Example: [{ from: 'ae', to: 'ä' }, { from: '-', to: ' '}] Default: [{ from: '-', to: ' ' }] */
  replaceCharacterList?: Array<CharacterMap> | undefined

  /** A transformation function that allows to customize the label strings. Receives the label string and has to return a string or React Component */
  transformLabel?: ((title: string) => React.ReactNode) | undefined

  /** Array containing all the indexes of the path that should be omitted and not be rendered as labels. If we have a path like '/home/category/1' then you might want to pass '[2]' here, which omits the breadcrumb label '1'. Indexes start with 0. Example: [2] Default: undefined */
  omitIndexList?: Array<number> | undefined

  /** An inline style object for the outer container */
  containerStyle?: CSSProperties | undefined

  /** Classes to be used for the outer container. Won't be used if useDefaultStyle is true */
  containerClassName?: string

  /** An inline style object for the breadcrumb list */
  listStyle?: CSSProperties | undefined

  /** Classes to be used for the breadcrumb list */
  listClassName?: string

  /** An inline style object for the inactive breadcrumb list item */
  inactiveItemStyle?: CSSProperties | undefined

  /** Classes to be used for the inactive breadcrumb list item */
  inactiveItemClassName?: string

  /** An inline style object for the active breadcrumb list item */
  activeItemStyle?: CSSProperties | undefined

  /** Classes to be used for the active breadcrumb list item */
  activeItemClassName?: string
}

const defaultProps: BreadcrumbsProps = {
  useDefaultStyle: false,
  rootLabel: 'Home',
  omitRootLabel: false,
  labelsToUppercase: false,
  replaceCharacterList: [{ from: '-', to: ' ' }],
  transformLabel: undefined,
  omitIndexList: undefined,
  containerStyle: undefined,
  containerClassName: '',
  listStyle: undefined,
  listClassName: '',
  inactiveItemStyle: undefined,
  inactiveItemClassName: '',
  activeItemStyle: undefined,
  activeItemClassName: '',
}

/**
 * A functional React component for Next.js that renders a dynamic Breadcrumb navigation
 * based on the current path within the Next.js router navigation.
 *
 * Only works in conjunction with Next.js, since it leverages the Next.js router.
 *
 * By setting useDefaultStyle to true, the default CSS will be used.
 * The component is highly customizable by either custom classes or
 * inline styles, which can be passed as props.
 *
 * @param props - object of type BreadcrumbsProps
 * @returns The breadcrumb React component.
 */
const Breadcrumbs = ({
  useDefaultStyle,
  rootLabel,
  omitRootLabel,
  labelsToUppercase,
  replaceCharacterList,
  transformLabel,
  omitIndexList,
  containerStyle,
  containerClassName,
  listStyle,
  listClassName,
  inactiveItemStyle,
  inactiveItemClassName,
  activeItemStyle,
  activeItemClassName,
}: BreadcrumbsProps = defaultProps) => {
  const router = useRouter()
  const [breadcrumbs, setBreadcrumbs] = useState<Array<Breadcrumb> | null>(null)

  useEffect(() => {
    if (router) {
      const linkPath = router.asPath.split('/')
      linkPath.shift()

      const pathArray = linkPath.map((path, i) => {
        return {
          breadcrumb: path,
          href: '/' + linkPath.slice(0, i + 1).join('/'),
        }
      })

      setBreadcrumbs(pathArray)
    }
  }, [router])

  if (!breadcrumbs) {
    return null
  }

  return (
    <>
      <BreadCrumbsStyles />
      <nav style={containerStyle} className={containerClassName} aria-label="breadcrumbs">
        <ol style={listStyle} className={useDefaultStyle ? '_2jvtI' : listClassName}>
          {!omitRootLabel && (
            <li style={inactiveItemStyle} className={inactiveItemClassName}>
              <Link href="/">
                {convertBreadcrumb(
                  rootLabel || 'Home',
                  labelsToUppercase,
                  replaceCharacterList,
                  transformLabel,
                )}
              </Link>
            </li>
          )}
          {breadcrumbs.length >= 1 &&
            breadcrumbs.map((breadcrumb, i) => {
              if (
                !breadcrumb ||
                breadcrumb.breadcrumb.length === 0 ||
                (omitIndexList && omitIndexList.find((value) => value === i))
              ) {
                return
              }
              return (
                <li
                  key={breadcrumb.href}
                  className={
                    i === breadcrumbs.length - 1 ? activeItemClassName : inactiveItemClassName
                  }
                  style={i === breadcrumbs.length - 1 ? activeItemStyle : inactiveItemStyle}
                >
                  <Link href={breadcrumb.href}>
                    {convertBreadcrumb(
                      breadcrumb.breadcrumb,
                      labelsToUppercase,
                      replaceCharacterList,
                      transformLabel,
                    )}
                  </Link>
                </li>
              )
            })}
        </ol>
      </nav>
    </>
  )
}

// Breadcrumbs.defaultProps = defaultProps;

export default Breadcrumbs
